import {Alert, Col, Collapse, Row, Spin, Popover, Typography} from 'antd'
import {InfoCircleOutlined} from '@ant-design/icons'
import TemperatureChart from './target-dashboard/temperature-chart'
import {useEffect, useMemo, useState} from 'react'
import {CARDS_GUTTER} from 'helpers/dashboards'
import {GET} from 'helpers/api'
import {useSelector} from 'react-redux'
import moment from 'moment'
import TemperatureDetails from './target-dashboard/temperature-details'
import {calcInterval} from 'helpers/chart'
import {calculateEndDate} from 'helpers/dates'
import ThermalRow from './gateway-dashboard/thermal-row'
import {TemperatureImageGallery} from './temperature-image-gallery'
import React from 'react'

export const TemperatureEventAnalysis = ({targetsview, period}) => {
  const [temperatures, setTemperatures] = useState(null)
  const [temperature, setTemperature] = useState(null)
  const [interval, setInterval] = useState(null)
  const token = useSelector(state => state.auth.token)

  useEffect(() => {
    const loadData = async interval => {
      let data = []
      let response = {meta: {page: 0}}
      do {
        response = await getTemperaturesview(interval.name, +response.meta.page + 1)
        data = data.concat(response.temperaturesview)
      } while (moreData(response.meta))
      // Add timestamp field to each object
      data = data.map(obj => {
        return {timestamp: obj.interval, max: obj.max_max, min: obj.min_min, ...obj}
      })
      setTemperatures(data)
      setTemperature(data[0])
      setInterval(interval)
    }

    if (period) {
      loadData(calcInterval(period))
    } else {
      setTemperatures(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsview, period])

  const periodDisplayRange = useMemo(() => {
    console.log('period', period)
    if (period?.start && period?.end) {
      const rangeStart = period?.start.split('T')[0]
      const rangeEnd = new moment(period.end).add(-1, 'days').toISOString().split('T')[0]
      return rangeStart === rangeEnd ? rangeStart : rangeStart + ' - ' + rangeEnd
    } else {
      return ''
    }
  }, [period])

  const thermalImagePeriod = useMemo(() => {
    if (temperature && interval) {
      const start = new Date(temperature.timestamp)
      const end = calculateEndDate(start, interval.value)
      const startMoment = moment(start)
      const endMoment = moment(end)
      const isSameDay = startMoment.isSame(endMoment, 'day')

      if (isSameDay) {
        const dateStr = startMoment.format('YYYY-MM-DD')
        const startTime = startMoment.format('HH:mm')
        const endTime = endMoment.format('HH:mm')
        return `${dateStr} ${startTime} - ${endTime}`
      } else {
        const startStr = startMoment.format('YYYY-MM-DD HH:mm')
        const endStr = endMoment.format('YYYY-MM-DD HH:mm')
        return `${startStr} - ${endStr}`
      }
    }
    return null
  }, [temperature, interval])

  const moreData = meta => {
    return meta.page * meta.pageSize < meta.total
  }

  const getTemperaturesview = (interval, page) => {
    const groupBy = `tg_id,${interval}`
    const request = GET(token)
    return fetch(
      process.env.REACT_APP_BACKEND_URL +
        `/temperaturesview?tg_id=${targetsview.tg_id}&start=${period.start}&end=${period.end}&count=${groupBy}&page=${page}&limit=1000`,
      request
    )
      .then(async res => {
        return res.json()
      })
      .then(response => {
        return response
      })
  }

  if (targetsview && temperatures && temperatures.length > 0) {
    return (
      <>
        <Row gutter={CARDS_GUTTER} style={{margin: '0em'}} align="middle">
          <Col xs={24} md={5}>
            <TemperatureDetails temperature={temperature} interval={interval} />
          </Col>
          <Col xs={24} md={19}>
            <TemperatureChart
              targetsview={targetsview}
              temperatures={temperatures}
              setTemperature={setTemperature}
              height={300}
            />
          </Col>
        </Row>
        <Collapse defaultActiveKey={['2']}>
          <Collapse.Panel header="Temperature Image Gallery (First 4 After Start)" key="2">
            <TemperatureImageGallery temperature={temperature} interval={interval} tgid={targetsview?.tg_id} />
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <span>
                Thermal Images for Selected Range{' '}
                <Popover
                  title="Understanding Thermal Image Aggregation"
                  content={
                    <div style={{maxWidth: 400}}>
                      <p>
                        <strong>Composite Images:</strong> Each thermal image displayed is a composite or aggregate
                        image created from multiple temperature events.
                      </p>
                      <p>
                        <strong>Pixel Values:</strong> Each pixel in the image represents an aggregated temperature
                        value (maximum, minimum, median, or mean) calculated from all temperature events that occurred
                        at that location during the selected time period.
                      </p>
                      <p>
                        <strong>Time Period:</strong> The period is determined by your selection on the temperature
                        graph above. All temperature events within this selected range are used to create the aggregated
                        images.
                      </p>
                      <p>
                        <strong>Visualization:</strong> These images help you visualize temperature patterns and
                        identify hotspots over time, showing how temperatures vary across the target area during the
                        selected period.
                      </p>
                    </div>
                  }
                  trigger="click"
                >
                  <span
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    style={{display: 'inline-block'}}
                  >
                    <InfoCircleOutlined style={{marginLeft: 8, color: '#1890ff', cursor: 'help'}} />
                  </span>
                </Popover>
              </span>
            }
            key="1"
          >
            <Typography.Paragraph
              style={{
                marginBottom: 16,
                color: 'rgba(0, 0, 0, 0.65)',
                fontSize: '14px',
                textAlign: 'center'
              }}
            >
              Composite thermal images showing aggregated temperature values (max/min/median/mean) at each pixel from
              all events in the selected period
              {thermalImagePeriod && (
                <span style={{display: 'block', marginTop: 4, fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)'}}>
                  Period: {thermalImagePeriod}
                </span>
              )}
            </Typography.Paragraph>
            <ThermalRow targetsview={targetsview} temperature={temperature} interval={interval} />
          </Collapse.Panel>
        </Collapse>
      </>
    )
  } else if (!temperatures) {
    return (
      <div style={{minHeight: '500px'}}>
        <Spin />
      </div>
    )
  } else if (temperatures?.length === 0) {
    return (
      <Alert
        message={`There are no target temperatures for this date range [${periodDisplayRange}]`}
        type="info"
        style={{margin: '20px auto', textAlign: 'center', backgroundColor: 'var(--blue-20'}}
      />
    )
  }
}

export default TemperatureEventAnalysis
